import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { screenAPI } from './screenAPI';
import { handleSnackbar } from '@/shared/ui';
import type {
  BulkRegisterResult,
  ScreenFilterDef,
  SlotItem,
} from '@/entities/screen/types';

interface ScreensState {
  screens: {
    headers: any[];
    rows: any[];
    totalItems: number;
    page: number;
    rowsPerPage: number;
    search?: string;
  };
  screenFilters: ScreenFilterDef[];
  selectedScreenFilters: Record<string, any>;
  selectedScreen: any | null;
  loading: boolean;
  previewMedia: string | null;
  mediaPreviewModal: {
    open: boolean;
    items: SlotItem[];
  };
  bulkResultDialog: {
    open: boolean;
    result: BulkRegisterResult | null;
  };
}

const initialState: ScreensState = {
  screens: {
    headers: [],
    rows: [],
    totalItems: 0,
    page: 0,
    rowsPerPage: 10,
    search: '',
  },
  screenFilters: [],
  selectedScreenFilters: {},
  selectedScreen: null,
  loading: false,
  previewMedia: null,
  mediaPreviewModal: {
    open: false,
    items: [],
  },
  bulkResultDialog: {
    open: false,
    result: null,
  },
};

// Async thunk for setting screen status
export const setScreenStatus = createAsyncThunk(
  'screen/setScreenStatus',
  async (
    {
      data,
      deviceId,
      callback,
    }: { data: any; deviceId: string; callback?: (success: boolean) => void },
    { dispatch }
  ) => {
    try {
      const response = await screenAPI.setScreenStatus(data, deviceId);

      if (response.success) {
        dispatch(
          handleSnackbar({
            open: true,
            message: data.approved
              ? 'Screen Approved Successfully'
              : 'Screen Rejected Successfully',
            severity: 'success',
            autoHideDuration: 2000,
          })
        );

        // Call callback immediately for immediate UI feedback
        callback?.(true);

        return response;
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Something went wrong';
      dispatch(
        handleSnackbar({
          open: true,
          message,
          severity: 'error',
          autoHideDuration: 3000,
        })
      );
      callback?.(false);
      throw error;
    }
  }
);

export const registerDevice = createAsyncThunk(
  'screen/registerDevice',
  async (
    {
      values,
      callback,
    }: {
      values: any;
      callback?: (result: {
        success: boolean;
        deviceCode?: string;
        deviceId?: string;
      }) => void;
    },
    { dispatch }
  ) => {
    try {
      const response = await screenAPI.registerDevice(values);
      const successList = response.data?.success ?? [];
      const failedList = response.data?.failed ?? [];

      if (successList.length > 0) {
        const registered = successList[0];
        const deviceCode = registered.deviceCode;
        const deviceId = registered.deviceId;

        dispatch(
          handleSnackbar({
            open: true,
            message: response.message,
            severity: 'success',
            autoHideDuration: 3000,
          })
        );

        if (callback) callback({ success: true, deviceCode, deviceId });
      } else if (failedList.length > 0 || response.success === false) {
        const reason =
          failedList[0]?.reason || response.message || 'Registration failed';

        dispatch(
          handleSnackbar({
            open: true,
            message: reason,
            severity: 'error',
            autoHideDuration: 5000,
          })
        );

        if (callback) callback({ success: false });
      } else {
        dispatch(
          handleSnackbar({
            open: true,
            message: response.message,
            severity: 'success',
            autoHideDuration: 3000,
          })
        );

        if (callback) callback({ success: true });
      }

      return response;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Something went wrong';
      if (callback) callback({ success: false });
      dispatch(
        handleSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
          autoHideDuration: 3000,
        })
      );
      throw err;
    }
  }
);

export const editDevice = createAsyncThunk(
  'screen/editDevice',
  async (
    {
      deviceId,
      values,
      callback,
    }: {
      deviceId: string;
      values: any;
      callback?: (success: boolean) => void;
    },
    { dispatch }
  ) => {
    try {
      const response = await screenAPI.editDevice(deviceId, values);
      if (response.success === false) {
        dispatch(
          handleSnackbar({
            open: true,
            message: response.message,
            severity: 'error',
            autoHideDuration: 3000,
          })
        );
        callback?.(false);
        return response;
      }

      dispatch(
        handleSnackbar({
          open: true,
          message: response.message,
          severity: 'success',
          autoHideDuration: 3000,
        })
      );
      callback?.(true);
      return response;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Something went wrong';
      dispatch(
        handleSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
          autoHideDuration: 3000,
        })
      );
      callback?.(false);
      throw err;
    }
  }
);

const screenSlice = createSlice({
  name: 'screen',
  initialState,
  reducers: {
    setScreens: (
      state,
      action: PayloadAction<Partial<ScreensState['screens']>>
    ) => {
      state.screens = { ...state.screens, ...action.payload };
    },
    setScreenFilters: (state, action: PayloadAction<ScreenFilterDef[]>) => {
      state.screenFilters = action.payload;
    },
    setSelectedScreenFilters: (
      state,
      action: PayloadAction<Record<string, any>>
    ) => {
      state.selectedScreenFilters = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setPagination: (
      state,
      action: PayloadAction<{ page?: number; rowsPerPage?: number }>
    ) => {
      if (action.payload.page !== undefined) {
        state.screens.page = action.payload.page;
      }
      if (action.payload.rowsPerPage !== undefined) {
        state.screens.rowsPerPage = action.payload.rowsPerPage;
      }
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.screens.search = action.payload;
    },
    setSelectedScreen: (state, action: PayloadAction<any>) => {
      state.selectedScreen = action.payload;
    },
    setPreviewMedia: (state, action: PayloadAction<string | null>) => {
      state.previewMedia = action.payload;
    },
    setMediaPreviewModal: (
      state,
      action: PayloadAction<{ open: boolean; items?: SlotItem[] }>
    ) => {
      state.mediaPreviewModal.open = action.payload.open;
      if (action.payload.items !== undefined) {
        state.mediaPreviewModal.items = action.payload.items;
      }
    },
    setBulkResultDialog: (
      state,
      action: PayloadAction<{
        open: boolean;
        result?: BulkRegisterResult | null;
      }>
    ) => {
      state.bulkResultDialog.open = action.payload.open;
      if (action.payload.result !== undefined) {
        state.bulkResultDialog.result = action.payload.result;
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(setScreenStatus.pending, state => {
        state.loading = true;
      })
      .addCase(setScreenStatus.fulfilled, () => {
        // Keep loading true for 4 seconds to allow Elasticsearch to populate
        // Note: We'll handle the loading state reset in the component instead
        // to avoid proxy revocation issues
      })
      .addCase(setScreenStatus.rejected, state => {
        state.loading = false;
      })
      .addCase(registerDevice.pending, state => {
        state.loading = true;
      })
      .addCase(registerDevice.fulfilled, state => {
        state.loading = false;
      })
      .addCase(registerDevice.rejected, state => {
        state.loading = false;
      })
      .addCase(editDevice.pending, state => {
        state.loading = true;
      })
      .addCase(editDevice.fulfilled, state => {
        state.loading = false;
      })
      .addCase(editDevice.rejected, state => {
        state.loading = false;
      });
  },
});

export const {
  setScreens,
  setScreenFilters,
  setSelectedScreenFilters,
  setLoading,
  setPagination,
  setSearch,
  setSelectedScreen,
  setPreviewMedia,
  setMediaPreviewModal,
  setBulkResultDialog,
} = screenSlice.actions;

export default screenSlice.reducer;
